/**
 * TalentRow — TAL-1
 *
 * Individual talent row component.
 * Shows name, status badge, tier tag, category icon.
 * Admin mode: LOC estimate, assigned agent badge.
 * Tenant mode: [Link] / [Tier ↑] action buttons.
 */

import React from 'react';

export interface TalentEntry {
    name: string;
    slug: string;
    category: string;
    source: string;      // "built" | "wiring" | "planned"
    status: string;
    tier_gate: string | null;
    description: string | null;
    estimated_loc: number | null;
    assigned_to: string | null;
    existing_service: string | null;
    priority: string | null;
}

interface TalentRowProps {
    entry: TalentEntry;
    mode: 'admin' | 'tenant';
}

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
    verified: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'READY' },
    talent: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'ACTIVE' },
    draft: { bg: 'bg-gray-500/10', text: 'text-gray-400', label: 'DRAFT' },
    disabled: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'DISABLED' },
    wiring: { bg: 'bg-amber-500/10', text: 'text-amber-400', label: 'WIRING' },
    planned: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'PLANNED' },
    dispatched: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'IN PROGRESS' },
    connected: { bg: 'bg-green-500/10', text: 'text-green-400', label: 'CONNECTED' },
    not_connected: { bg: 'bg-gray-500/10', text: 'text-gray-500', label: 'NOT LINKED' },
    coming_soon: { bg: 'bg-gray-800/50', text: 'text-gray-600', label: 'COMING SOON' },
};

const CATEGORY_ICONS: Record<string, string> = {
    connector: '🔌',
    skill: '⚡',
    sink: '📤',
    governance: '🛡',
    cron: '⏰',
    engine: '🏭',
    marketplace_seed: '🌱',
};

export const TalentRow: React.FC<TalentRowProps> = ({ entry, mode }) => {
    const statusStyle = STATUS_COLORS[entry.status] || STATUS_COLORS['draft'];
    const categoryIcon = CATEGORY_ICONS[entry.category] || '📦';

    return (
        <div className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-800/20 rounded group transition-colors">
            {/* Status dot */}
            <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.text === 'text-green-400' ? 'bg-green-400' : statusStyle.text === 'text-amber-400' ? 'bg-amber-400' : statusStyle.text === 'text-blue-400' ? 'bg-blue-400' : statusStyle.text === 'text-purple-400' ? 'bg-purple-400' : 'bg-gray-600'}`} />

            {/* Category icon */}
            <span className="text-xs" title={entry.category}>{categoryIcon}</span>

            {/* Name */}
            <span className="text-xs font-mono text-gray-300 flex-1 truncate">
                {entry.name}
            </span>

            {/* Status badge */}
            <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${statusStyle.bg} ${statusStyle.text}`}>
                {statusStyle.label}
            </span>

            {/* Admin-only: LOC estimate + assigned agent */}
            {mode === 'admin' && entry.estimated_loc && (
                <span className="text-[9px] font-mono text-gray-600">
                    ~{entry.estimated_loc} LOC
                </span>
            )}
            {mode === 'admin' && entry.assigned_to && (
                <span className="text-[9px] font-mono text-purple-400/60 bg-purple-500/10 px-1 rounded">
                    {entry.assigned_to}
                </span>
            )}

            {/* Tenant-only: tier gate badge / action */}
            {mode === 'tenant' && entry.tier_gate && (
                <span className="text-[9px] font-mono text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded cursor-pointer hover:bg-cyan-500/20">
                    Tier ↑ {entry.tier_gate}
                </span>
            )}
        </div>
    );
};
